import { useState, useEffect, useRef, useCallback } from 'react';
import { getForecastAssumptions, putForecastAssumption } from '../api/forecastAssumptions';

/* Hypothèses du Prévisionnel (BFRE + cash flow) persistées en BASE par tenant ×
   année. Chargement au montage ; écriture par clé (PUT debouncé ~500 ms) → pas de
   rafale réseau pendant la frappe. Les setters acceptent une valeur ou un updater
   (prev => next), comme setState, pour les objets/listes du cash flow. */

const DEFAULTS = { dso: '', dpo: '', dio: '', pac: '', cf_inv: [], cf_fin: [] };

export function useForecastAssumptions(tenantId, year) {
  const [a, setA] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const timers = useRef({});

  const reload = useCallback(() => {
    if (!tenantId) { setA(DEFAULTS); setLoading(false); return; }
    getForecastAssumptions(tenantId, year)
      .then((r) => setA({ ...DEFAULTS, ...((r && r.data) || {}) }))
      .catch(() => {});
  }, [tenantId, year]);

  useEffect(() => {
    let alive = true;
    if (!tenantId) { setA(DEFAULTS); setLoading(false); return undefined; }
    setLoading(true);
    getForecastAssumptions(tenantId, year)
      .then((r) => { if (alive) setA({ ...DEFAULTS, ...((r && r.data) || {}) }); })
      .catch(() => { if (alive) setA(DEFAULTS); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [tenantId, year]);

  const setKey = useCallback((key, updater) => {
    setA((prev) => {
      const value = typeof updater === 'function' ? updater(prev[key]) : updater;
      clearTimeout(timers.current[key]);
      timers.current[key] = setTimeout(() => {
        if (tenantId) putForecastAssumption(tenantId, year, key, value).catch(() => {});
      }, 500);
      return { ...prev, [key]: value };
    });
  }, [tenantId, year]);

  /* ÉCRITURE IMMÉDIATE, et qui se signale (2026-09-19). `setKey` diffère de
     500 ms, ce qu'il faut pour de la FRAPPE ; un interrupteur se clique une
     fois, et l'écran qui en dépend doit se recharger juste après. Sans cette
     promesse, basculer « Ventes : Connecté » ne changeait rien à l'écran tant
     qu'on ne rafraîchissait pas la page. */
  const setKeyNow = useCallback((key, value) => {
    clearTimeout(timers.current[key]);
    setA((prev) => ({ ...prev, [key]: value }));
    if (!tenantId) return Promise.resolve();
    return putForecastAssumption(tenantId, year, key, value).catch(() => {});
  }, [tenantId, year]);

  return { a, loading, setKey, setKeyNow, reload };
}
